import steps from "pages/NewDashboard/steps";
import stepsTur from "pages/NewDashboard/stepsTur";
import { APP_MESSAGES, APP_MESSAGES_TUR } from "./constants";

export const APP_LANGUAGE_CODES = ["en", "tr", "it", "es", "de", "ar"];

export const APP_LANGUAGES = [
  { code: "en", nativeLabel: "English" },
  { code: "tr", nativeLabel: "Türkçe" },
  { code: "it", nativeLabel: "Italiano" },
  { code: "es", nativeLabel: "Español" },
  { code: "de", nativeLabel: "Deutsch" },
  { code: "ar", nativeLabel: "العربية" },
];

const INTL_LOCALE_MAP = {
  en: "en-US",
  tr: "tr-TR",
  it: "it-IT",
  es: "es-ES",
  de: "de-DE",
  ar: "ar-SA",
};

/** Normalize any language string to a supported app language code. */
export function normalizeAppLanguage(language) {
  const code = String(language || "en")
    .toLowerCase()
    .split("-")[0];
  return APP_LANGUAGE_CODES.includes(code) ? code : "en";
}

/** Marketing-site deep link: /signup?lang=tr (also accepts lng). */
export function languageFromSearch(search) {
  if (!search) return null;
  const params = new URLSearchParams(
    String(search).startsWith("?") ? search : `?${search}`
  );
  const raw = params.get("lang") || params.get("lng");
  if (!raw) return null;
  return normalizeAppLanguage(raw);
}

/** Query lang wins so a Turkish website visit is not stuck on a stored EN preference. */
export function resolveInitialAppLanguage({ search = "", stored = null } = {}) {
  const fromQuery = languageFromSearch(search);
  if (fromQuery) return fromQuery;
  if (stored) return normalizeAppLanguage(stored);
  return "en";
}

export function readStoredAppLanguage() {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem("userLang");
    return stored ? normalizeAppLanguage(stored) : null;
  } catch {
    return null;
  }
}

export function persistAppLanguage(language) {
  const code = normalizeAppLanguage(language);
  if (typeof window === "undefined") return code;
  try {
    window.localStorage.setItem("userLang", code);
  } catch {
    // ignore quota / private mode
  }
  applyDocumentLanguage(code);
  return code;
}

/** Marketing + verify-email continue URLs must keep the chosen locale. */
export function withAppLanguageOnUrl(href, language) {
  const code = normalizeAppLanguage(language);
  try {
    const url = new URL(
      href,
      typeof window !== "undefined"
        ? window.location.origin
        : "https://app.pivony.com"
    );
    url.searchParams.set("lang", code);
    return url.toString();
  } catch {
    return href;
  }
}

export function resolvePreferredAppLanguage({
  search = "",
  stored = null,
  i18nLanguage = null,
} = {}) {
  return resolveInitialAppLanguage({
    search,
    stored: stored || (i18nLanguage ? normalizeAppLanguage(i18nLanguage) : null),
  });
}

/**
 * After login: account language beats a stale stored EN preference.
 * On /signup and /login, an explicit ?lang= still wins (marketing-site deep link).
 */
export function resolveAuthenticatedAppLanguage({
  search = "",
  pathname = "",
  accountLanguage = null,
  stored = null,
  i18nLanguage = null,
} = {}) {
  const path = String(pathname || "").split("?")[0];
  const onAuthPage = path === "/signup" || path === "/login";
  const fromQuery = languageFromSearch(search);
  if (onAuthPage && fromQuery) return fromQuery;
  if (accountLanguage) return normalizeAppLanguage(accountLanguage);
  if (fromQuery) return fromQuery;
  if (stored) return normalizeAppLanguage(stored);
  if (i18nLanguage) return normalizeAppLanguage(i18nLanguage);
  return "en";
}

/** BCP 47 locale for dates, numbers, and Intl formatters. */
export function getIntlLocale(language) {
  const code = normalizeAppLanguage(language);
  return INTL_LOCALE_MAP[code] || INTL_LOCALE_MAP.en;
}

/** Locale-aware page headers/subheaders (TR uses APP_MESSAGES_TUR). */
export function getAppMessages(language) {
  return normalizeAppLanguage(language) === "tr" ? APP_MESSAGES_TUR : APP_MESSAGES;
}

/** Locale-aware number formatting for review counts, quotas, etc. */
export function formatAppNumber(value, language) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? "");
  return new Intl.NumberFormat(getIntlLocale(language)).format(n);
}

/** Dashboard creation wizard steps (only TR has a dedicated file today). */
export function getWizardStepsConfig(language) {
  return normalizeAppLanguage(language) === "tr" ? stepsTur : steps;
}

export function getDateTimeFormat(language) {
  const code = normalizeAppLanguage(language);
  const formats = {
    tr: "DD MMM YYYY HH:mm",
    en: "MMM D, YYYY h:mm A",
    it: "DD MMM YYYY HH:mm",
    de: "DD.MM.YYYY HH:mm",
    es: "DD MMM YYYY HH:mm",
    ar: "DD MMM YYYY HH:mm",
  };
  return formats[code] || formats.en;
}

/** Parse YYYY-MM-DD (or ISO) as a local calendar date to avoid UTC day shifts. */
function parseLocalDate(iso) {
  if (iso == null || iso === "") return null;
  const match = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatSingleDateLabel(date, locale, options) {
  return date.toLocaleDateString(locale, options);
}

/** Wrap a formatted since/until pair with localized "from … to …" connectors. */
function withFromToConnectors(startLabel, endLabel, language) {
  const code = normalizeAppLanguage(language);
  const templates = {
    en: (a, b) => `from ${a} to ${b}`,
    tr: (a, b) => `${a} ile ${b} arası`,
    it: (a, b) => `da ${a} a ${b}`,
    es: (a, b) => `de ${a} a ${b}`,
    de: (a, b) => `von ${a} bis ${b}`,
    ar: (a, b) => `من ${a} إلى ${b}`,
  };
  return (templates[code] || templates.en)(startLabel, endLabel);
}

/**
 * Locale-aware date range label for filter chips.
 * TR: "27 Temmuz ile 28 Temmuz 2026 arası"
 * EN: "from July 27 to July 28, 2026"
 */
export function formatDateRangeLabel(since, until, language) {
  if (!since && !until) return "";

  const locale = getIntlLocale(language);
  const start = parseLocalDate(since);
  const end = parseLocalDate(until);
  const full = { day: "numeric", month: "long", year: "numeric" };
  const dayMonth = { day: "numeric", month: "long" };

  if (!start && !end) {
    return withFromToConnectors(String(since), String(until), language);
  }
  if (!start) {
    return formatSingleDateLabel(end, locale, full);
  }
  if (!end) {
    return formatSingleDateLabel(start, locale, full);
  }

  const startLabel =
    start.getFullYear() === end.getFullYear()
      ? formatSingleDateLabel(start, locale, dayMonth)
      : formatSingleDateLabel(start, locale, full);
  const endLabel = formatSingleDateLabel(end, locale, full);

  return withFromToConnectors(startLabel, endLabel, language);
}

/** Sync <html lang/dir> for RTL (Arabic) and accessibility. */
export function applyDocumentLanguage(language) {
  const code = normalizeAppLanguage(language);
  if (typeof document === "undefined") return;
  document.documentElement.lang = code;
  document.documentElement.dir = code === "ar" ? "rtl" : "ltr";
}

/** Topic Library industry seed uses full English language names in tag_lang. */
const INDUSTRY_TOPIC_LANGUAGE_NAMES = {
  en: "English",
  tr: "Turkish",
  it: "Italian",
  es: "Spanish",
  de: "German",
  ar: "Arabic",
};

export function getIndustryTopicLanguageName(language) {
  const code = normalizeAppLanguage(language);
  return INDUSTRY_TOPIC_LANGUAGE_NAMES[code] || INDUSTRY_TOPIC_LANGUAGE_NAMES.en;
}
